import { useCallback, useState } from "react";
import { getRouteViaStateUpdate } from "../utils/getRouteViaStateUpdate";
import { isRouteViaCMM } from "../utils/isRouteViaCMM";

interface AsteraPrimaryCareSubmissionData {
  task: string;
  mrn: string;
  key: string;
  outcome: string;
  routeVia: string;
  poc: string;
  comments: string;
}

export enum AsteraPrimaryCareSubmissionDataKeys {
  TASK = "task",
  MRN = "mrn",
  KEY = "key",
  OUTCOME = "outcome",
  ROUTE_VIA = "routeVia",
  POC = "poc",
  COMMENTS = "comments",
}

const resolveField = (value: any): string => {
  if (value == null) return "";
  if (typeof value === "object" && "data" in value) {
    return value.data ?? "";
  }
  return String(value);
};

const getInitialState = (
  initialData?: Record<string, any> | null,
): AsteraPrimaryCareSubmissionData => {
  const defaultState: AsteraPrimaryCareSubmissionData = {
    task: "",
    mrn: "",
    key: "",
    outcome: "",
    routeVia: "",
    poc: "",
    comments: "",
  };
  if (!initialData) return defaultState;
  return {
    ...defaultState,
    task:
      resolveField(initialData.case_name) ||
      resolveField(initialData.task) ||
      "",
    mrn:
      resolveField(initialData.patient_mrn) ||
      resolveField(initialData.mrn) ||
      "",
    key:
      resolveField(initialData.cmm_result_key) ||
      resolveField(initialData.covermymed_id) ||
      resolveField(initialData.key) ||
      "",
    outcome:
      resolveField(initialData.outcome) ||
      resolveField(initialData.response_status) ||
      "",
    routeVia:
      resolveField(initialData.routeVia) ||
      resolveField(initialData.route_via) ||
      "",
    poc: resolveField(initialData.poc) || "",
    comments: resolveField(initialData.comments) || "",
  };
};

export const useAsteraPrimaryCareSubmissionData = (
  initialData?: Record<string, any> | null,
) => {
  const [submissionData, setSubmissionData] =
    useState<AsteraPrimaryCareSubmissionData>(() =>
      getInitialState(initialData),
    );

  const handleDataChange = useCallback((id: string, value: string) => {
    setSubmissionData((prev) => ({
      ...prev,
      [id]: value,
    }));
  }, []);

  const handleOutcomeChange = useCallback((id: string) => {
    setSubmissionData((prev) => ({
      ...prev,
      [AsteraPrimaryCareSubmissionDataKeys.OUTCOME]: id,
    }));
  }, []);

  const handleRouteViaChange = useCallback((id: string) => {
    setSubmissionData((prev) => ({
      ...prev,
      ...getRouteViaStateUpdate(id),
    }));
  }, []);

  const isKeyValid = isRouteViaCMM(submissionData.routeVia)
    ? submissionData.key !== ""
    : submissionData.key === "";

  const isFormValid =
    isKeyValid &&
    submissionData.outcome !== "" &&
    submissionData.routeVia !== "" &&
    submissionData.poc !== "";

  return {
    submissionData,
    handleDataChange,
    handleOutcomeChange,
    handleRouteViaChange,
    isFormValid,
  };
};
